import React from "react";
import { Heading } from "@v2/components/display/Heading/Heading";
import { IUserFull } from "@gettactic/api";
import { cn, formatStartEndSlotHours } from "@gettactic/helpers/src";
import { Button } from "@v2/components/ui/button";
import { FavoriteIcon } from "@v2/icons";
import { ISchedule } from "@gettactic/api";
import { Badge } from "@v2/components/ui/badge";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { useAskTessa } from "@v2/components/ai/AskTessaProvider";
import { useIsMobile } from "@v2/hooks/use-mobile";

export interface UserLabelProps {
  user: Partial<IUserFull>;
  className?: string;
  allowFavorite?: boolean;
  allowTessa?: boolean;
  showTitle?: boolean;
  showEmail?: boolean;
  variant?: "default" | "dropdown" | "gantt";
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  showAvatar?: boolean;
  schedules?: ISchedule[];
  allowTruncate?: boolean;
  isOrgPrivacyEnabled?: boolean;
  currentUserId?: string;
}

const classes = {
  Wrapper: "group flex items-center",
  InfoWrapper: "flex w-full flex-1 flex-col",
  UserInfo: "text-muted-foreground",
  FavoriteWrapper:
    "self-start opacity-0 transition-opacity group-hover:opacity-100",
  FavoriteWrapperMobile: "self-start opacity-1 transition-opacity",
  UserName: "flex items-center justify-between"
};

const variantClasses = {
  default: {
    Wrapper: "h-[86px] gap-4",
    AvatarWrapper: "h-[70px] w-[70px]",
    Avatar: "h-[70px] w-[70px]",
    InfoWrapper: "gap-1.5",
    UserName: "",
    UserInfo: "text-[12px]"
  },
  dropdown: {
    Wrapper: "h-[47px] gap-2.5",
    AvatarWrapper: "h-[28px] w-[28px]",
    Avatar: "h-[28px] w-[28px]",
    InfoWrapper: "gap-0",
    UserName: "font-[400] text-[13px]",
    UserInfo: "text-[11px]"
  },
  gantt: {
    Wrapper: "gap-2.5",
    AvatarWrapper: "h-[26px] w-[26px]",
    Avatar: "h-[26px] w-[26px]",
    InfoWrapper: "gap-0",
    UserName: "font-[400] text-[13px]",
    UserInfo: "text-[11px]"
  }
};

export const UserLabel: React.FC<UserLabelProps> = ({
  user,
  className,
  allowFavorite = false,
  allowTessa = false,
  showTitle = true,
  showEmail = false,
  variant = "default",
  onClick,
  onMouseEnter,
  onMouseLeave,
  showAvatar = true,
  schedules,
  allowTruncate = false,
  isOrgPrivacyEnabled = false,
  currentUserId
}) => {
  const selectedClasses = variantClasses[variant || "default"];
  const tessa = useAskTessa();
  const isMobile = useIsMobile();

  // Hide user information when privacy is enabled and it's not the current user
  const shouldHideUserInfo = isOrgPrivacyEnabled && user.id !== currentUserId;

  return (
    <div
      className={cn(
        classes.Wrapper,
        selectedClasses.Wrapper,
        className,
        allowTruncate && "min-w-0"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {showAvatar && (
        <div className={selectedClasses.AvatarWrapper}>
          {shouldHideUserInfo ? (
            <UserAvatar
              user={{
                name: "Private",
                id: "N/A",
                email: "N/A",
                profile_photo_url: null,
                roles: ["deleted"]
              }}
              className={cn(selectedClasses.Avatar)}
            />
          ) : (
            <UserAvatar user={user} className={selectedClasses.Avatar} />
          )}
        </div>
      )}
      <div
        className={cn("flex flex-1 items-center", allowTruncate && "min-w-0")}
      >
        <div
          className={cn(
            classes.InfoWrapper,
            selectedClasses.InfoWrapper,
            allowTruncate && "min-w-0"
          )}
        >
          <Heading
            as={"h5"}
            className={cn(
              !allowTruncate && classes.UserName,
              selectedClasses.UserName,
              allowTruncate && "truncate"
            )}
          >
            {shouldHideUserInfo
              ? "Private"
              : user.name +
                (user.roles?.includes("deleted") ? " (inactive)" : "")}
          </Heading>
          {showTitle && (
            <p className={cn(classes.UserInfo, selectedClasses.UserInfo)}>
              {shouldHideUserInfo ? "Private" : user.title}
            </p>
          )}
          {showEmail && !shouldHideUserInfo && (
            <p className={cn(classes.UserInfo, selectedClasses.UserInfo)}>
              {user.email}
            </p>
          )}
          {schedules && schedules.length > 0 && (
            <p
              className={cn(
                classes.UserInfo,
                selectedClasses.UserInfo,
                "text-[13px] text-black",
                "flex items-center gap-2"
              )}
            >
              {formatStartEndSlotHours(schedules[0], " → ")}
              {schedules.length > 1 && (
                <Badge variant={"outline"} className={"text-muted-foreground"}>
                  {schedules.length - 1} more
                </Badge>
              )}
            </p>
          )}
        </div>
        {/* RESTORE IT LATER WHEN RELESE TESSA */}
        {/*{tessa && allowTessa && (
          <div
            className={cn(
              isMobile ? classes.FavoriteWrapperMobile : classes.FavoriteWrapper
            )}
          >
            <Button
              variant={"icon"}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();

                tessa?.open({
                  targets: [
                    {
                      id: user.id || "",
                      type: "user",
                      label: user.name || "User"
                    }
                  ]
                });
              }}
            >
              <AiIcon className={"size-3.5"} />
            </Button>
          </div>
        )}*/}
        {allowFavorite && (
          <div
            className={cn(
              isMobile ? classes.FavoriteWrapperMobile : classes.FavoriteWrapper
            )}
          >
            <Button variant={"icon"}>
              <FavoriteIcon className={"size-3.5"} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
